import * as Sentry from "@sentry/react"
import { debugExperiments } from "utils/debugExperiments"
import { diagnosticsRecorder } from "./recorder"

/**
 * Captures a user-triggered diagnostic snapshot and sends it to Sentry
 * with attached Session Replay.
 *
 * Throws if Sentry SDK is not initialized (caller should toast.error).
 */
export const sendDiagnosticReport = async (): Promise<void> => {
  const snapshot = diagnosticsRecorder.snapshot()

  // Force-flush replay so the last 0-60s of DOM events are attached
  // to the upcoming event. getReplay() returns undefined if replay
  // integration is not registered.
  const replay = Sentry.getReplay()
  if (replay) {
    try {
      await replay.flush()
    } catch {
      // Non-fatal — proceed to captureMessage anyway.
    }
  }

  Sentry.withScope((scope) => {
    scope.setLevel("warning")
    scope.setTag("report_type", "user_diagnostic")
    if (snapshot.meta.telegramId !== null) {
      scope.setTag("telegram_id", String(snapshot.meta.telegramId))
    }
    const enabledExperiments = debugExperiments.getEnabledList()
    if (enabledExperiments.length > 0) {
      scope.setTag("experiments", enabledExperiments.join(","))
    }
    scope.setExtras({
      diagnostics: snapshot
    })
    // Duplicate key arrays into structured contexts — Sentry UI renders contexts
    // as collapsible panels, much friendlier than navigating nested extras.diagnostics.*.
    scope.setContext("memory_timeline", { samples: snapshot.memory })
    scope.setContext("img_errors", { errors: snapshot.imgErrors })
    scope.setContext("longtasks", { tasks: snapshot.longtasks })
    Sentry.captureMessage("User-triggered diagnostic report", "warning")
  })

  // Ensure the event leaves the SDK queue before the WebView might suspend.
  await Sentry.flush(2000).catch(() => undefined)
}
